import { useCallback, useMemo } from 'react';
import {
  ButtonWrapper,
  EventMenuBody,
  EventMenuRoot,
  LeftSideContent,
  LogoContainer,
  LogoImage,
  RightSideContent,
} from './styles';

import weLogo from '@assets/images/weCONECT_col_RGB.svg';
import Button from '@components/Button';
import MainMenuItemDesktop from '@composites/MainMenuItemDesktop';
import { MenuItem as MenuItemType } from '@hubs101/booking-api-skd-client/lib/types';
import { getEventDetails } from '@store/slices/eventSlice';
import { getImageSrc } from '@utils/fn';
import { useDesktopWidth } from '@utils/hooks';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { APP_HOST, usePublicCoLocatedEventsByEventId } from '@utils/apiHooks';
import Skeleton from 'react-loading-skeleton';
import WeMenuItem from '@composites/WeMenuItem';

export const mappingLinks = {
  home: '',
  agenda: 'agenda-url',
  speaker: 'speakers-url',
  partner: 'partners-url',
  'who-why': 'who-&-why-url',
  'content-hub': 'content-hub-url',
  faq: 'faq-url',
};

const EventMenu = ({
  TopHeaderComponent,
}: {
  TopHeaderComponent?: JSX.Element | null;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const isDesktop = useDesktopWidth();

  const { data: coLocatedEvents, isLoading: isCoLocatedEventsLoading } =
    usePublicCoLocatedEventsByEventId({
      eventId: sluggedEvent?.id,
      enabled: sluggedEvent?.id?.length > 0 && sluggedEvent?.colocated,
    });

  const sortedMenuItems = useMemo(
    () =>
      sluggedEvent?.website_menu
        ?.find(e => e.type === 'mainmenu')
        ?.items?.toSorted(
          (a: MenuItemType, b: MenuItemType) => a.order - b.order
        ),
    [sluggedEvent?.website_menu]
  );

  const link = useCallback(
    (item: MenuItemType) => {
      switch (item.type) {
        case 'internal':
          return `/${item.url}`;
        case 'external':
          return item.url;
        default:
          return t(
            mappingLinks[item.type as keyof typeof mappingLinks] ??
              `/${item.url}`
          );
      }
    },
    [t]
  );

  const mainMenuItems = useMemo(
    () =>
      sortedMenuItems
        ?.filter(e => !e.level)
        ?.slice(0, 6)
        ?.map(item => {
          return {
            id: item.label,
            title: item.label,
            subTitle: item.label,
            link: link(item),
            order: item.order,
          };
        }),
    [link, sortedMenuItems]
  );

  const secondaryMenuItems = useMemo(
    () =>
      sortedMenuItems
        ?.filter(e => e.level)
        ?.map(item => {
          return {
            id: item.label,
            title: item.label,
            subTitle: item.label,
            link: link(item),
            order: item.order,
            index: mainMenuItems?.findLastIndex(
              element => element.order < item.order
            ),
          };
        }) ?? [],
    [link, mainMenuItems, sortedMenuItems]
  );

  if (isDesktop) return null;

  return (
    <>
      {TopHeaderComponent}
      <EventMenuRoot>
        <EventMenuBody>
          <LeftSideContent>
            <LogoContainer $size={4}>
              <LogoImage
                $maxWidth={'15.6rem'}
                src={
                  sluggedEvent?.design?.eventLogoPosSvg?.length > 0
                    ? getImageSrc(sluggedEvent?.design?.eventLogoPosSvg)
                    : weLogo
                }
                alt={
                  sluggedEvent?.name?.length
                    ? sluggedEvent?.name
                    : 'WeConect Logo'
                }
                $height={`${4}rem`}
                onClick={() => {
                  window.location.href = `${APP_HOST}/events/${sluggedEvent?.slug}`;
                }}
              />
            </LogoContainer>

            {mainMenuItems?.map((item, index) => (
              <MainMenuItemDesktop
                key={item.id}
                title={item.title}
                link={item.link}
                subMenuItems={secondaryMenuItems?.filter(
                  item => item.index === index
                )}
              />
            ))}
          </LeftSideContent>

          <RightSideContent>
            {sluggedEvent?.colocated && (
              <>
                {isCoLocatedEventsLoading ? (
                  <Skeleton
                    count={1}
                    height="100%"
                    width="15rem"
                    enableAnimation
                    baseColor={sluggedEvent?.design?.secondaryThemeColor}
                  />
                ) : sluggedEvent?.webinar ? (
                  <WeMenuItem
                    title={t('co-located-events')}
                    coLocatedEvents={coLocatedEvents}
                    backgroundColor={sluggedEvent?.design?.secondaryThemeColor}
                    hoverBackgroundColor={theme.color.strokeGrey}
                  />
                ) : (
                  <ButtonWrapper>
                    <Button
                      title={t('exhibit-now')}
                      backgroundColor={sluggedEvent?.design?.themeColor}
                      hoverBackgroundColor={theme.color.mobilityGrey}
                      textColor={theme.color.white}
                      hoverTextColor={theme.color.white}
                      borderRadius={theme.borderRadius.radius10}
                      fontSize={theme.fontSize.size20}
                      lineHeight={theme.lineHeight.height150}
                      padding={'0.5rem 2.9rem'}
                      width="max-content"
                      onClick={() => {
                        window.location.href = `${window.location.origin}/events/${sluggedEvent?.slug}${t('bp-packages-url')}`;
                      }}
                    />
                  </ButtonWrapper>
                )}
              </>
            )}

            <ButtonWrapper>
              <Button
                title={t('book-now')}
                backgroundColor={sluggedEvent?.design?.themeColor}
                hoverBackgroundColor={theme.color.mobilityGrey}
                textColor={theme.color.white}
                hoverTextColor={theme.color.white}
                borderRadius={theme.borderRadius.radius10}
                fontSize={theme.fontSize.size20}
                lineHeight={theme.lineHeight.height150}
                padding={'0.5rem 2.9rem'}
                width="max-content"
                onClick={() => {
                  window.location.href = `${window.location.origin}/events/${sluggedEvent?.slug}${t('book-now-url')}`;
                }}
              />
            </ButtonWrapper>
          </RightSideContent>
        </EventMenuBody>
      </EventMenuRoot>
    </>
  );
};

export default EventMenu;
